import {
  KeyMilestonesTableRow,
  KpiChartConfig,
  KpiChartConstants,
  KpiChartId,
  KpiKeys
} from '@models';
import { featureFlags } from 'environments/environment';

const KPI_KEYS_TO_CHART_TYPES_MAP: Partial<Record<KpiKeys, KpiChartConfig[]>> = {
  active: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barActiveByCountryToTarget', type: 'bar' },
    {
      id: 'toggleActiveByStage',
      type: 'toggleChart'
    },
    { id: 'barActiveByDaysInStage', type: 'bar' }
  ],
  enrollment: [
    { id: 'trendChart', type: 'trendchart' },
    {
      id: 'toggleEnrollmentByCountry',
      type: 'toggleChart'
    },
    { id: 'barEnrollmentByOrganization', type: 'bar' }
  ],
  earlyterm: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barEarlyTermByCountry', type: 'bar' },
    { id: 'barEarlyTermBySite', type: 'bar' },
    {
      id: 'toggleEarlyTermByReason',
      type: 'toggleChart'
    }
  ],
  enrolled: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barEnrolledByCountry', type: 'bar' },
    { id: 'barEnrolledByOrganization', type: 'bar' }
  ],
  subject_screen_fail_rate: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barSubjectScreenFailRateByCountry', type: 'bar' },
    { id: 'barSubjectScreenFailRateBySite', type: 'bar' },
    {
      id: 'toggleSubjectScreenFailRateByReason',
      type: 'toggleChart'
    }
  ],
  deviation_rate_per_subject: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barDeviationRatePerSubjectByCountry', type: 'bar' },
    { id: 'barDeviationRatePerSubjectBySite', type: 'bar' },
    {
      id: 'toggleDeviationRatePerSubjectByReason',
      type: 'toggleChart'
    }
  ],
  days_to_close_site_findings: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barDaysToCloseSiteFindingsBySite', type: 'bar' },
    {
      id: 'toggleDaysToCloseSiteFindingsByTimeRange',
      type: 'toggleChart'
    }
  ],
  clinical_material_kits_shipped: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barClinicalMaterialKitsShippedByCountry', type: 'bar' },
    { id: 'barClinicalMaterialKitsShippedBySite', type: 'bar' }
  ],
  crf_review_completed: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barCrfReviewCompletedByCountry', type: 'bar' },
    { id: 'barCrfReviewCompletedBySite', type: 'bar' }
  ],
  on_time_dispatch_clinical_materials: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barOnTimeDispatchClinicalMaterialsBySite', type: 'bar' }
  ],
  key_milestones_on_track_to_baseline: [
    { id: 'timeCycleChart', type: 'timeCycleChart' },
    { id: 'keyMilestonesTable', type: 'keyMilestonesTable' }
  ],
  days_to_pay_sites: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barDaysToPaySitesByCountry', type: 'bar' },
    { id: 'barDaysToPaySitesBySite', type: 'bar' },
    {
      id: 'toggleDaysToPaySitesByTimeRange',
      type: 'toggleChart'
    }
  ],
  source_data_verification: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barSdvComplianceByCountry', type: 'bar' },
    { id: 'barSdvComplianceBySite', type: 'bar' }
  ],
  lab_sample_query_rate: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barLabSampleQueryRateByCountry', type: 'bar' },
    { id: 'barLabSampleQueryRateBySite', type: 'bar' },
    {
      id: 'toggleLabSampleQueryRateByQueryType',
      type: 'toggleChart'
    }
  ],
  ereq_usage_for_lab_samples: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barEreqUsageForLabSamplesByCountry', type: 'bar' },
    { id: 'barEreqUsageForLabSamplesBySite', type: 'bar' },
    {
      id: 'toggleEreqUsageForLabSamplesBySampleType',
      type: 'toggleChart'
    },
    { id: 'barEreqUsageForLabSamplesByRequestType', type: 'bar' }
  ],
  lab_kits_shipped: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barLabKitsShippedByCountry', type: 'bar' },
    { id: 'barLabKitsShippedBySite', type: 'bar' },
    {
      id: 'toggleLabKitsShippedByKitType',
      type: 'toggleChart'
    }
  ],
  lab_kit_wastage: [
    { id: 'trendChart', type: 'trendchart' },
    { id: 'barLabKitWastageByCountry', type: 'bar' },
    { id: 'barLabKitWastageBySite', type: 'bar' },
    { id: 'toggleLabKitWastageByKitType', type: 'toggleChart' }
  ]
};

const KPI_CHART_CONSTANTS: Partial<Record<KpiChartId, KpiChartConstants>> = {
  barActiveByCountryToTarget: {
    titleSuffix: 'By country',
    customChartTitle: 'Site activation by country',
    xAxisLabel: 'Activated sites',
    sortValue: 'desc',
    tooltipText: '% of sites activated by country to target',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  toggleActiveByStage: {
    titleSuffix: 'By activation stage',
    customChartTitle: 'Site activation by stage',
    tooltipText: '# of sites by activation stage',
    charts: {
      primaryChart: {
        id: 'barActiveByStage',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieActiveByStage',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barActiveByStage: {
    titleSuffix: 'By activation stage',
    customChartTitle: 'Site activation by stage',
    xAxisLabel: 'Site count',
    sortValue: 'asc',
    tooltipText: '# of sites by activation stage',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  barActiveByDaysInStage: {
    titleSuffix: 'Days in stage',
    customChartTitle: 'Site activation by days in stage',
    xAxisLabel: 'Days in stage',
    sortValue: 'alphabetical',
    tooltipText: '# of days in stage',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  barEnrolledByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Percent complete',
    sortValue: 'desc',
    tooltipText: '% of enrolled subjects by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: [],
    customChartTitle: 'Subjects enrolled by country'
  },

  barEnrolledByOrganization: {
    titleSuffix: 'By organization',
    xAxisLabel: 'Count of subjects enrolled',
    sortValue: 'desc',
    tooltipText: '# of enrolled subjects by organization',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: [],
    customChartTitle: 'Subjects enrolled by organization'
  },

  pieActiveByStage: {
    titleSuffix: 'By activation stage',
    customChartTitle: 'Site activation by stage',
    sortValue: 'asc',
    tooltipText: '% of active subjects by stage',
    fieldFilter: 'siteStatus',
    legendValues: []
  },

  groupedBarEnrollmentByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Site count',
    sortValue: 'desc',
    tooltipText: '# of sites by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: [
      {
        text: 'Non-enrolling sites',
        color: '#25a1be'
      },
      {
        text: 'Total activated sites',
        color: '#005DAA'
      }
    ]
  },

  barEnrollmentByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Percent complete',
    sortValue: 'desc',
    tooltipText: '% of sites by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  toggleEnrollmentByCountry: {
    titleSuffix: 'By country',
    tooltipText: '# of sites by country',
    charts: {
      primaryChart: {
        id: 'groupedBarEnrollmentByCountry',
        type: 'groupedBar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'barEnrollmentByCountry',
        type: 'bar',
        label: 'Percent'
      }
    }
  },

  barEnrollmentByOrganization: {
    titleSuffix: 'By days since activation',
    xAxisLabel: 'Days since activation',
    sortValue: 'desc',
    tooltipText: '# of days since activation by organization',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barSubjectScreenFailRateByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Screen fail rate',
    sortValue: 'desc',
    tooltipText: 'Screen fail rate by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barSubjectScreenFailRateBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Screen fail rate',
    sortValue: 'desc',
    tooltipText: 'Screen fail rate by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for subject screen failures',
    customChartTitle: 'Reason for screen failure',
    charts: {
      primaryChart: {
        id: 'barSubjectScreenFailRateByReason',
        type: 'bar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'pieSubjectScreenFailRateByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Subject Screen Fail distribution',
    sortValue: 'desc',
    tooltipText: '# Subject screen failures by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for screen failure'
  },

  pieSubjectScreenFailRateByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of subject screen failures by reason',
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for screen failure'
  },

  barEarlyTermByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Early terminations',
    sortValue: 'desc',
    tooltipText: 'Early terminations by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barEarlyTermBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Early terminations',
    sortValue: 'desc',
    tooltipText: 'Early terminations by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleEarlyTermByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for early terminations',
    customChartTitle: 'Reason for early termination',
    charts: {
      primaryChart: {
        id: 'barEarlyTermByReason',
        type: 'bar',
        label: 'Count'
      },
      secondaryChart: {
        id: 'pieEarlyTermByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barEarlyTermByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Subject early terminated distribution',
    sortValue: 'desc',
    tooltipText: '% early terminations by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for early termination'
  },

  pieEarlyTermByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of early terminations by reason',
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for early termination'
  },

  barDeviationRatePerSubjectByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Deviation rate',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barDeviationRatePerSubjectBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Deviation rate',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  toggleDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    tooltipText: 'Reason for deviations',
    customChartTitle: 'Reason for deviation',
    charts: {
      primaryChart: {
        id: 'barDeviationRatePerSubjectByReason',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieDeviationRatePerSubjectByReason',
        type: 'pie',
        label: 'Percent'
      }
    }
  },

  barDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    xAxisLabel: 'Deviation rate',
    sortValue: 'desc',
    tooltipText: 'Deviation rate by reason',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for deviation'
  },

  pieDeviationRatePerSubjectByReason: {
    titleSuffix: 'Reason',
    sortValue: 'desc',
    tooltipText: '% of deviation rate by reason',
    fieldFilter: '',
    legendValues: [],
    customChartTitle: 'Reason for deviation'
  },

  barClinicalMaterialKitsShippedByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Clinical material kits shipped by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barClinicalMaterialKitsShippedBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Clinical material kits shipped by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barCrfReviewCompletedByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'CRFs reviewed',
    sortValue: 'desc',
    tooltipText: 'CRF review completion by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: [],
    customChartTitle: 'CRF review completion by country'
  },

  barCrfReviewCompletedBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'CRFs reviewed',
    sortValue: 'desc',
    tooltipText: 'CRF review completion by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: [],
    customChartTitle: 'CRF review completion by site'
  },

  barOnTimeDispatchClinicalMaterialsBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'On-time dispatch',
    sortValue: 'desc',
    tooltipText: 'On-time dispatch by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barSdvComplianceByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'SDV compliance (%)',
    sortValue: 'desc',
    tooltipText: 'Source data verification (SDV) compliance by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: [],
    customChartTitle: 'Source data verification (SDV) compliance by country'
  },

  barSdvComplianceBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'SDV compliance (%)',
    sortValue: 'desc',
    tooltipText: 'Source data verification (SDV) compliance by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: [],
    customChartTitle: 'Source data verification (SDV) compliance by site'
  },

  barDaysToCloseSiteFindingsBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Average days to close findings',
    sortValue: 'desc',
    tooltipText: 'Average days to close findings by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barDaysToCloseSiteFindingsByTimeRange: {
    titleSuffix: 'By time ranges',
    xAxisLabel: 'Findings count',
    sortValue: 'asc',
    tooltipText: 'Findings count by time range',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },

  pieDaysToCloseSiteFindingsByTimeRange: {
    titleSuffix: 'By time ranges',
    sortValue: 'asc',
    tooltipText: 'Findings count by time range',
    fieldFilter: '',
    legendValues: []
  },

  toggleDaysToCloseSiteFindingsByTimeRange: {
    titleSuffix: 'By time ranges',
    tooltipText: 'Count',
    charts: {
      primaryChart: {
        id: 'barDaysToCloseSiteFindingsByTimeRange',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieDaysToCloseSiteFindingsByTimeRange',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barDaysToPaySitesByCountry: {
    titleSuffix: 'By country',
    xAxisLabel: 'Days to pay',
    sortValue: 'desc',
    tooltipText: 'Average days to pay by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barDaysToPaySitesBySite: {
    titleSuffix: 'By site',
    xAxisLabel: 'Days to pay',
    sortValue: 'desc',
    tooltipText: 'Average days to pay by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barDaysToPaySitesByTimeRange: {
    titleSuffix: 'By time ranges',
    xAxisLabel: 'Site payments count',
    sortValue: 'asc',
    tooltipText: 'Site payments count by time range',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },

  pieDaysToPaySitesByTimeRange: {
    titleSuffix: 'By time ranges',
    sortValue: 'asc',
    tooltipText: 'Site payments count by time range',
    fieldFilter: '',
    legendValues: []
  },

  toggleDaysToPaySitesByTimeRange: {
    titleSuffix: 'By time ranges',
    tooltipText: 'Count',
    charts: {
      primaryChart: {
        id: 'barDaysToPaySitesByTimeRange',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieDaysToPaySitesByTimeRange',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barLabSampleQueryRateByCountry: {
    titleSuffix: 'By country',
    customChartTitle: 'Query rate per visit by country',
    xAxisLabel: 'Query rate',
    sortValue: 'desc',
    tooltipText: 'Query rate by country',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barLabSampleQueryRateBySite: {
    titleSuffix: 'By site',
    customChartTitle: 'Query rate per visit by site',
    xAxisLabel: 'Query rate',
    sortValue: 'desc',
    tooltipText: 'Query rate by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barLabSampleQueryRateByQueryType: {
    titleSuffix: 'By query type',
    customChartTitle: 'Lab sample queries by type',
    xAxisLabel: 'Query count',
    sortValue: 'desc',
    tooltipText: 'Query count by type',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'gclExceptionType',
    legendValues: []
  },

  pieLabSampleQueryRateByQueryType: {
    titleSuffix: 'By query type',
    customChartTitle: 'Lab sample queries by type',
    sortValue: 'asc',
    tooltipText: 'Query count by type',
    fieldFilter: 'gclExceptionType',
    legendValues: []
  },

  toggleLabSampleQueryRateByQueryType: {
    titleSuffix: 'By query type',
    customChartTitle: 'Lab sample queries by type',
    tooltipText: 'Query count by type',
    charts: {
      primaryChart: {
        id: 'barLabSampleQueryRateByQueryType',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieLabSampleQueryRateByQueryType',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barEreqUsageForLabSamplesByCountry: {
    titleSuffix: 'By country',
    customChartTitle: 'eReq usage for lab samples by country',
    xAxisLabel: 'eReq usage',
    sortValue: 'desc',
    tooltipText: 'eReq usage for lab samples by country',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barEreqUsageForLabSamplesBySite: {
    titleSuffix: 'By site',
    customChartTitle: 'eReq usage for lab samples by site',
    xAxisLabel: 'eReq usage',
    sortValue: 'desc',
    tooltipText: 'eReq usage for lab samples by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barEreqUsageForLabSamplesBySampleType: {
    titleSuffix: 'By sample type',
    customChartTitle: 'eReq samples by sample type',
    xAxisLabel: 'Sample type count',
    sortValue: 'desc',
    tooltipText: 'eReq samples by sample type',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'sampleType',
    legendValues: []
  },

  pieEreqUsageForLabSamplesBySampleType: {
    titleSuffix: 'By sample type',
    customChartTitle: 'eReq samples by sample type',
    sortValue: 'asc',
    tooltipText: 'eReq samples by sample type',
    fieldFilter: 'sampleType',
    legendValues: []
  },

  toggleEreqUsageForLabSamplesBySampleType: {
    titleSuffix: 'By sample type',
    customChartTitle: 'eReq samples by sample type',
    tooltipText: 'eReq samples by sample type',
    charts: {
      primaryChart: {
        id: 'barEreqUsageForLabSamplesBySampleType',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieEreqUsageForLabSamplesBySampleType',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barEreqUsageForLabSamplesByRequestType: {
    titleSuffix: 'By request type',
    customChartTitle: 'Query rate per type of request',
    xAxisLabel: 'Query rate',
    sortValue: 'desc',
    tooltipText: 'Query rate by type of request',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: '',
    legendValues: []
  },
  barLabKitsShippedByCountry: {
    titleSuffix: 'By country',
    customChartTitle: 'Lab kits shipped by country',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Lab kits shipped by country',
    labelCount: { small: 5, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barLabKitsShippedBySite: {
    titleSuffix: 'By site',
    customChartTitle: 'Lab kits shipped by site',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Lab kits shipped by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },
  barLabKitsShippedByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits shipped by kit type',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Lab kits shipped by kit type',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'kitType',
    legendValues: []
  },
  pieLabKitsShippedByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits shipped by kit type',
    sortValue: 'asc',
    tooltipText: 'Lab kits shipped by kit type',
    fieldFilter: 'kitType',
    legendValues: []
  },

  toggleLabKitsShippedByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits shipped by kit type',
    tooltipText: 'Lab kits shipped by kit type',
    charts: {
      primaryChart: {
        id: 'barLabKitsShippedByKitType',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieLabKitsShippedByKitType',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  },

  barLabKitWastageByCountry: {
    titleSuffix: 'By country',
    customChartTitle: 'Lab kit wastage rate by country',
    xAxisLabel: 'Wastage',
    sortValue: 'desc',
    tooltipText: 'Lab kit wastage by country',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'isoStdCountryName',
    legendValues: []
  },

  barLabKitWastageBySite: {
    titleSuffix: 'By site',
    customChartTitle: 'Lab kit wastage rate by site',
    xAxisLabel: 'Wastage',
    sortValue: 'desc',
    tooltipText: 'Lab kit wastage by site',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'siteOrganization',
    legendValues: []
  },

  barLabKitWastageByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits wastage by kit type',
    xAxisLabel: 'Kits shipped',
    sortValue: 'desc',
    tooltipText: 'Kits shipped by type',
    labelCount: { small: 3, medium: 5 },
    fieldFilter: 'kitType',
    legendValues: []
  },

  pieLabKitWastageByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits wastage by kit type',
    sortValue: 'asc',
    tooltipText: 'Kits shipped by type',
    fieldFilter: 'kitType',
    legendValues: []
  },

  toggleLabKitWastageByKitType: {
    titleSuffix: 'By kit type',
    customChartTitle: 'Lab kits wastage by kit type',
    tooltipText: 'Kits shipped by type',
    charts: {
      primaryChart: {
        id: 'barLabKitWastageByKitType',
        type: 'bar',
        label: 'Bar chart'
      },
      secondaryChart: {
        id: 'pieLabKitWastageByKitType',
        type: 'pie',
        label: 'Pie chart'
      }
    }
  }
};

const KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP: Record<keyof KeyMilestonesTableRow, string> = {
  milestoneName: 'Milestone name',
  baselineDate: 'Baseline date',
  plannedDate: 'Planned date',
  actualDate: 'Actual date',
  baselineVariance: 'Baseline variance',
  status: 'Health'
};

const KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID: KpiChartId[] = [
  'barEnrolledByCountry',
  'barEnrolledByOrganization',
  'barActiveByCountryToTarget',
  'barActiveByStage',
  'barActiveByDaysInStage',
  'barEnrollmentByOrganization',
  'barEnrollmentByCountry',
  'groupedBarEnrollmentByCountry',
  'barSubjectScreenFailRateByCountry',
  'barSubjectScreenFailRateBySite',
  'barSubjectScreenFailRateByReason',
  'barEarlyTermByCountry',
  'barEarlyTermBySite',
  'barEarlyTermByReason',
  'barDeviationRatePerSubjectByCountry',
  'barDeviationRatePerSubjectBySite',
  'barDeviationRatePerSubjectByReason',
  'barClinicalMaterialKitsShippedByCountry',
  'barClinicalMaterialKitsShippedBySite',
  'barCrfReviewCompletedByCountry',
  'barCrfReviewCompletedBySite',
  'barOnTimeDispatchClinicalMaterialsBySite',
  'barSdvComplianceByCountry',
  'barSdvComplianceBySite',
  'barDaysToCloseSiteFindingsBySite',
  'barDaysToCloseSiteFindingsByTimeRange',
  'barDaysToPaySitesByCountry',
  'barDaysToPaySitesBySite',
  'barDaysToPaySitesByTimeRange'
];

const KPI_EXCLUDES_ON_TRACK_IN_COUNT: KpiKeys[] = ['subject_screen_fail_rate'];

const KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP: Record<string, string> = {
  study_deviation: 'Study Deviation',
  recovery_from_indication: 'Recovery from Indication',
  death: 'Death',
  progressive_disease: 'Progressive Disease',
  lack_of_efficacy: 'Lack of Efficacy',
  inclusion_criteria: 'Inclusion Criteria',
  terminated_by_sponsor: 'Terminated by Sponsor',
  adverse_event: 'Adverse Event',
  non_compliance: 'Non-Compliance',
  physician_decision: 'Physician Decision',
  withdrawn_by_subject: 'Withdrawn by Subject',
  pregnancy: 'Pregnancy',
  lost_to_follow_up: 'Lost to Follow Up',
  unknown: 'Unknown',
  exclusion_criteria: 'Exclusion Criteria'
};

const KPI_EARLY_TERM_REASON_MAP: Record<string, string> = {
  adverse_event: 'Adverse Event',
  death: 'Death',
  exclusion_criteria: 'Exclusion Criteria',
  inclusion_criteria: 'Inclusion Criteria',
  lack_of_efficacy: 'Lack of Efficacy',
  lost_to_follow_up: 'Lost to Follow Up',
  non_compliance: 'Non-Compliance',
  physician: 'Physician',
  decision: 'Decision',
  pregnancy: 'Pregnancy',
  progressive_disease: 'Progressive Disease',
  recovery_from_indication: 'Recovery from Indication',
  study_deviation: 'Study Deviation',
  terminated_by_sponsor: 'Terminated by Sponsor',
  unknown: 'Unknown',
  withdrawn_by_subject: 'Withdrawn by Subject'
};

const KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP: Record<string, string> = {
  study_intervention: 'Study Intervention',
  ich_gcp_deviation: 'ICH/GCP Deviation',
  ich_gcp_issue: 'ICH/GCP Issue',
  protocol_deviation: 'Protocol Deviation',
  informed_consent: 'Informed Consent',
  discontinuation: 'Discontinuation',
  inclusion_exclusion: 'Inclusion/Exclusion',
  trial_procedures: 'Trial Procedures',
  safety_reporting: 'Safety Reporting',
  prohibited_medication_or_treatment: 'Prohibited Medication or Treatment'
};

const KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP: Record<string, string> = {
  ereq_samples: 'Ereq samples',
  non_ereq_samples: 'Non-Ereq samples'
};

// Helper function to filter out hidden KPIs from chart types map
function filterHiddenKpisFromChartMap(
  chartMap: Partial<Record<KpiKeys, KpiChartConfig[]>>
): Partial<Record<KpiKeys, KpiChartConfig[]>> {
  const filtered = { ...chartMap };

  // Remove entire KPI entries for hiddenKPIs
  featureFlags.hiddenKPIs.forEach((hiddenKey) => {
    delete filtered[hiddenKey as KpiKeys];
  });

  // For hiddenDrilldownCharts, keep trend chart but remove all other chart types
  featureFlags.hiddenDrilldownCharts.forEach((hiddenKey) => {
    const kpiKey = hiddenKey as KpiKeys;
    if (filtered[kpiKey]) {
      // Keep only the trend chart, filter out all other chart types (bar, pie, toggle, etc.)
      filtered[kpiKey] = filtered[kpiKey]!.filter(
        (chartConfig) => chartConfig.type === 'trendchart'
      );
    }
  });

  return filtered;
}

// Apply filtering to KPI_KEYS_TO_CHART_TYPES_MAP
const FILTERED_KPI_KEYS_TO_CHART_TYPES_MAP = filterHiddenKpisFromChartMap(
  KPI_KEYS_TO_CHART_TYPES_MAP
);

const TIME_RANGE_MAP: Record<string, string> = {
  on_target: 'On Track',
  target_at_risk: 'At Risk',
  off_target: 'Off Track',
  data_not_available: 'Data Not Available'
};

export {
  KEY_MILESTONES_TABLE_COLUMN_KEY_LABEL_MAP,
  KPI_CHART_CONSTANTS,
  KPI_CHARTS_WITH_INCREASED_BOTTOM_GRID,
  KPI_DEVIATION_RATE_PER_SUBJECT_REASON_MAP,
  KPI_EARLY_TERM_REASON_MAP,
  KPI_EREQ_USAGE_FOR_LAB_SAMPLES_BY_REQUEST_TYPE_MAP,
  KPI_EXCLUDES_ON_TRACK_IN_COUNT,
  FILTERED_KPI_KEYS_TO_CHART_TYPES_MAP as KPI_KEYS_TO_CHART_TYPES_MAP,
  KPI_SUBJECTS_SCREEN_FAIL_RATE_REASON_MAP,
  TIME_RANGE_MAP
};
